import cn from 'classnames';
import { forwardRef } from 'react';

interface Props {
  ellipsis?: boolean;
  iconEnd?: React.ReactNode;
  iconStart?: React.ReactNode;
  size?: 14 | 20 | 22 | 26 | 32;
  variant?: 'primary' | 'unstyled';
}

// onClick required
type ClickableChip = JSX.IntrinsicElements['button'] & Props;

// onClick forbidden
type DisplayChip = JSX.IntrinsicElements['span'] &
  Props & {
    disabled?: never;
    onClick?: never;
  };

export type Chip = DisplayChip | ClickableChip;

export const Chip = forwardRef<HTMLButtonElement | HTMLSpanElement, Chip>(
  (
    {
      children,
      className,
      disabled,
      ellipsis,
      iconEnd,
      iconStart,
      onClick,
      size = 32,
      variant = 'primary',
      ...props
    },
    ref,
  ) => {
    const isButton = Boolean(onClick);

    const ChipComponent = isButton ? 'button' : 'span';

    return (
      <ChipComponent
        // @ts-ignore
        ref={ref}
        className={cn(
          'flex min-w-0 items-center rounded-[16px] transition duration-200',
          {
            'border border-gray-200 bg-gray-200 text-gray-600':
              disabled && variant === 'primary',
            'border border-gray-300 bg-gray-300 text-gray-800':
              !disabled && variant === 'primary',
            'cursor-not-allowed': disabled,
            'cursor-pointer': isButton && !disabled,
            'h-3.5 min-h-3.5 gap-x-1 px-[5px] py-[1px] text-10/120% font-medium':
              size === 14,
            'h-5 min-h-5 gap-x-1 px-[5px] py-[3px] text-12/120% font-medium':
              size === 20,
            'h-8 min-h-8 gap-x-2 px-[11px] py-2 text-14/120% font-medium':
              size === 32,
            'hover:border-red-300 hover:bg-red-300 focus:border-red-300 focus:bg-red-300 active:border-red-500 active:bg-red-500':
              isButton && !disabled && variant === 'primary',

            'pe-2': Boolean(iconEnd),
            'ps-2': Boolean(iconStart),
            'text-body-100 h-[26px] min-h-[26px] gap-x-1 px-[9px] py-[5px]':
              size === 26,
            'text-heading-75 h-[22px] min-h-[22px] gap-x-1 px-[7px] py-[3px]':
              size === 22,
          },
          className,
        )}
        disabled={disabled}
        onClick={onClick}
        type="button"
        {...props}
      >
        {iconStart && (
          <span
            className={cn('shrink-0', {
              '[&>svg]:h-3 [&>svg]:w-3': size === 14,
              '[&>svg]:h-3.5 [&>svg]:w-3.5':
                size === 26 || size === 22 || size === 20,
              '[&>svg]:h-[18px] [&>svg]:w-[18px]': size === 32,
            })}
          >
            {iconStart}
          </span>
        )}
        <span
          className={cn('max-w-[210px]', {
            'text-single-line': ellipsis || isButton,
          })}
        >
          {children}
        </span>
        {iconEnd && (
          <span
            className={cn('shrink-0', {
              '[&>svg]:h-3 [&>svg]:w-3': size === 14,
              '[&>svg]:h-3.5 [&>svg]:w-3.5':
                size === 26 || size === 22 || size === 20,
              '[&>svg]:h-[18px] [&>svg]:w-[18px]': size === 32,
            })}
          >
            {iconEnd}
          </span>
        )}
      </ChipComponent>
    );
  },
);

Chip.displayName = 'Chip';
